"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
require("core-js/modules/es6.object.assign");
/*
# This component has four parts…

The Frame is the "box" of the button. It has one of four types:
"black", "gold", "white" or "outline". Frame sets a background color, and optional
shadow and a border. If there are any width considerations, they should be put
here. It should NOT have padding.

Inside the frame is either a StyledButton or StyledLink. The StyledButton
and StyledLink components provide a functional link around a Label. Their only
styling should be to expand to cancel out browser defaults, expand to the size
of the frame, and center their Label.

Type styling and foreground color go in the Label.

This should give us Buttons and Links that are shaped identically (Frame), work
differently (StyledButton or StyledLink), and have identical typography (Label).


Finally, there is an optional Wrapper. It is only rendered when the "centerLines"
attribute is activated. It is decorated with horizontal lines. We can split the
centering and the presence of decorated lines down the road if we like.

examples:

  <Button to={url} shadow >
    Read more
  </Button>

  <Button onClick={() => register()} type="gold" >
    Sign up for our newsletter
  </Button>

  <Button to={url} size="large" type="outline" centerLines>
    Read more
  </Button>

*/
const react_1 = __importDefault(require("react"));
const gatsby_1 = require("gatsby");
const styled_components_1 = __importDefault(require("styled-components"));
const _theme_1 = require("@theme");
const _utils_1 = require("@utils");
const atoms_1 = require("@components/atoms"); // Component
var Button = function Button(_ref) {
    var _ref$size = _ref.size, size = _ref$size === void 0 ? 'regular' : _ref$size, _ref$type = _ref.type, type = _ref$type === void 0 ? 'black' : _ref$type, color = _ref.color, _ref$shadow = _ref.shadow, shadow = _ref$shadow === void 0 ? false : _ref$shadow, _ref$center = _ref.center, center = _ref$center === void 0 ? false : _ref$center, _ref$lines = _ref.lines, lines = _ref$lines === void 0 ? false : _ref$lines, _ref$display = _ref.display, display = _ref$display === void 0 ? 'inline-flex' : _ref$display, _ref$invert = _ref.invert, invert = _ref$invert === void 0 ? false : _ref$invert, to = _ref.to, onClick = _ref.onClick, _ref$processing = _ref.processing, processing = _ref$processing === void 0 ? false : _ref$processing, disabled = _ref.disabled, className = _ref.className, children = _ref.children;
    var Button = /*#__PURE__*/ react_1.default.createElement(Frame, Object.assign({
        className: className
    }, {
        type: type,
        color: color,
        size: size,
        disabled: disabled,
        shadow: shadow,
        display: display,
        lines: lines,
        invert: invert
    }), to && to.substr(0, 1) === '/' && /*#__PURE__*/ react_1.default.createElement(StyledLink, Object.assign({
        to: to
    }, {
        size: size
    }), /*#__PURE__*/ react_1.default.createElement(Label, {
        type: type,
        size: size,
        invert: invert
    }, children)), to && to.substr(0, 1) !== '/' && /*#__PURE__*/ react_1.default.createElement(StyledAnchor, Object.assign({
        href: to,
        target: "_blank"
    }, {
        size: size
    }), /*#__PURE__*/ react_1.default.createElement(Label, {
        type: type,
        size: size,
        invert: invert
    }, children)), onClick && /*#__PURE__*/ react_1.default.createElement(StyledButton, Object.assign({
        onClick: onClick,
        disabled: disabled
    }, {
        size: size
    }), !processing && /*#__PURE__*/ react_1.default.createElement(Label, {
        type: type,
        size: size,
        disabled: disabled,
        invert: invert
    }, children), processing && /*#__PURE__*/ react_1.default.createElement(atoms_1.Spinner, null)));
    if (!center)
        return Button;
    if (!lines)
        return /*#__PURE__*/ react_1.default.createElement(Wrapper, null, Button);
    return /*#__PURE__*/ react_1.default.createElement(LinedButtonMargin, null, /*#__PURE__*/ react_1.default.createElement(Lines, {
        invert: invert
    }, /*#__PURE__*/ react_1.default.createElement(Wrapper, Object.assign({
        invert: invert
    }, {
        lines: true
    }), Button)));
}; // Styling
var Wrapper = styled_components_1.default.div.withConfig({
    displayName: "Button__Wrapper",
    componentId: "c7i75x-0"
})(["z-index:1;position:relative;display:flex;flex-direction:row;justify-content:center;background-color:", ";max-width:100%;", ""], function (props) {
    return !!props.invert ? _theme_1.palette.ink : _theme_1.palette.ivory;
}, function (props) {
    return props.lines && "\n    width: fit-content;\n    padding: 0 16px;\n\n    " + _utils_1.mediaQuery('tablet') + " {\n      padding: 0 24px;\n    }\n\n    " + _utils_1.mediaQuery('laptop') + " {\n      padding: 0 32px;\n    }\n\n    " + _utils_1.mediaQuery('desktop') + " {\n      padding: 0 40px;\n    }\n  ";
});
var LinedButtonMargin = styled_components_1.default.div.withConfig({
    displayName: "Button__LinedButtonMargin",
    componentId: "c7i75x-1"
})(["position:relative;padding:32px 0 64px;", "{padding:40px 0 80px;}", "{padding:48px 0 96px;}", "{padding:64px 0 128px;}"], _utils_1.mediaQuery('tablet'), _utils_1.mediaQuery('laptop'), _utils_1.mediaQuery('desktop'));
var Lines = styled_components_1.default(function (props) {
    return /*#__PURE__*/ react_1.default.createElement(atoms_1.Margin, props);
}).withConfig({
    displayName: "Button__Lines",
    componentId: "c7i75x-2"
})(["position:relative;display:flex;flex-direction:row;justify-content:center;&::before{content:' ';position:absolute;left:0px;top:50%;height:1px;width:calc((100% - 200px) / 2);background:", ";opacity:0.24;}&::after{content:' ';position:absolute;right:0px;top:50%;height:1px;width:calc((100% - 200px) / 2);background:", ";opacity:0.24;transform:rotateZ(180deg);}"], function (props) {
    return !!props.invert ? _theme_1.gradient.buttonLineLight : _theme_1.gradient.buttonLine;
}, function (props) {
    return !!props.invert ? _theme_1.gradient.buttonLineLight : _theme_1.gradient.buttonLine;
}); // Frame
var Frame = styled_components_1.default.div.withConfig({
    displayName: "Button__Frame",
    componentId: "c7i75x-3"
})(["position:relative;display:", ";justify-content:center;align-content:center;align-items:center;max-width:100%;background:", ";border:1px solid ", ";text-shadow:1px 1px 0px ", ";filter:contrast(100%);transform:translate3d(0,0,0) scale3d(1,1,1);cursor:", ";opacity:", ";transition:background-color 0.25s ease-in-out,border-color 0.25s ease-in-out,box-shadow 0.25s ease-in-out,filter 0.25s ease-in-out,opacity 0.25s ease-in-out,transform 0.40s ease-in-out;", " ", " height:", ";", "{&:hover{filter:contrast(", "%);transform:translate3d(", ") scale3d(", ");}}", "{", " ", " height:", ";}", "{height:", ";}"], function (props) {
    return props.display;
}, function (props) {
    return props.type === 'solid' ? props.color : props.type === 'black' ? _theme_1.gradient.blackButton : props.type === 'gold' ? _theme_1.gradient.goldButton : props.type === 'white' ? _theme_1.palette.snow : 'transparent';
}, function (props) {
    return props.type === 'black' ? _theme_1.palette.black : props.type === 'gold' ? _theme_1.palette.brown : props.type === 'white' ? _theme_1.palette.snow : props.type === 'outline' && props.invert ? _utils_1.rgba(_theme_1.palette.snow, 0.24) : props.type === 'outline' ? _theme_1.palette.ink : 'transparent';
}, function (props) {
    return props.type === 'black' ? 'rgba(0, 0, 0, 0.8)' : props.type === 'gold' ? 'rgba(150, 113, 23, 0.8)' : props.type === 'outline' && props.invert ? 'rgba(0, 0, 0, 0.8)' : 'transparent';
}, function (props) {
    return props.disabled ? 'not-allowed' : 'pointer';
}, function (props) {
    return props.disabled ? 0.24 : 1;
}, function (props) {
    return props.shadow && "\n    box-shadow: 0px 12px 18px " + _utils_1.rgba(_theme_1.palette.black, 0.16) + ";\n  ";
}, function (props) {
    return props.disabled && "\n    box-shadow: 0px 12px 18px " + _utils_1.rgba(_theme_1.palette.black, 0) + ";\n  ";
}, function (props) {
    return props.size === 'regular' ? '40px' : props.size === 'small' ? '30px' : props.size === 'large' ? '56px' : props.size === 'nav' ? '64px' : 0;
}, _utils_1.mediaQuery('tablet'), function (props) {
    return props.disabled ? 100 : 120;
}, function (props) {
    return props.disabled ? '0, 0, 0' : '0, -2px, 0';
}, function (props) {
    return props.disabled ? '1, 1, 1' : '1.015, 1.01, 1';
}, _utils_1.mediaQuery('laptop'), function (props) {
    return props.shadow && "\n      box-shadow: 0px 24px 32px " + _utils_1.rgba(_theme_1.palette.black, 0.16) + ";\n    ";
}, function (props) {
    return props.disabled && "\n      box-shadow: 0px 24px 32px " + _utils_1.rgba(_theme_1.palette.black, 0) + ";\n    ";
}, function (props) {
    return props.size === 'regular' ? '48px' : props.size === 'small' ? '32px' : props.size === 'large' ? '64px' : props.size === 'nav' ? '80px' : 0;
}, _utils_1.mediaQuery('desktop'), function (props) {
    return props.size === 'regular' ? '56px' : props.size === 'small' ? '36px' : props.size === 'large' ? '72px' : props.size === 'nav' ? '96px' : 0;
}); // Label
var Label = styled_components_1.default.span.withConfig({
    displayName: "Button__Label",
    componentId: "c7i75x-4"
})(["display:block;flex:0 1 auto;max-width:100%;width:100%;align-self:center;text-align:center;text-overflow:hidden;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;", ";color:", ";opacity:", ";transition:0.25s opacity ease-in-out;& span.seoCopy{display:none;}"], function (props) {
    return props.size === 'regular' ? _theme_1.type.label : props.size === 'small' ? _theme_1.type.labelSmall : props.size === 'large' ? _theme_1.type.labelLarge : props.size === 'nav' ? _theme_1.type.label : _theme_1.type.label;
}, function (props) {
    return props.type === 'white' ? _theme_1.palette.ink : props.type === 'outline' && props.invert ? _theme_1.palette.snow : props.type === 'outline' ? _theme_1.palette.ink : props.type === 'text' ? _theme_1.palette.gold : _theme_1.palette.snow;
}, function (props) {
    return props.disabled ? 0.8 : 1;
}); // Link
var StyledLink = styled_components_1.default(function (props) {
    return /*#__PURE__*/ react_1.default.createElement(gatsby_1.Link, props);
}).withConfig({
    displayName: "Button__StyledLink",
    componentId: "c7i75x-5"
})(["display:flex;align-items:center;justify-content:center;width:100%;height:100%;padding:", ";", "{padding:", ";}", "{padding:", ";}"], function (props) {
    return props.size === 'regular' ? '0px 24px' : props.size === 'small' ? '0px 16px' : props.size === 'large' ? '0px 40px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('laptop'), function (props) {
    return props.size === 'regular' ? '0px 32px' : props.size === 'small' ? '0px 24px' : props.size === 'large' ? '0px 48px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('desktop'), function (props) {
    return props.size === 'regular' ? '0px 40px' : props.size === 'small' ? '0px 32px' : props.size === 'large' ? '0px 56px' : props.size === 'nav' ? '0px 40px' : 0;
}); // Anchor
var StyledAnchor = styled_components_1.default.a.withConfig({
    displayName: "Button__StyledAnchor",
    componentId: "c7i75x-6"
})(["display:flex;align-items:center;justify-content:center;width:100%;height:100%;padding:", ";", "{padding:", ";}", "{padding:", ";}"], function (props) {
    return props.size === 'regular' ? '0px 24px' : props.size === 'small' ? '0px 16px' : props.size === 'large' ? '0px 40px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('laptop'), function (props) {
    return props.size === 'regular' ? '0px 32px' : props.size === 'small' ? '0px 24px' : props.size === 'large' ? '0px 48px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('desktop'), function (props) {
    return props.size === 'regular' ? '0px 40px' : props.size === 'small' ? '0px 32px' : props.size === 'large' ? '0px 56px' : props.size === 'nav' ? '0px 40px' : 0;
}); // Button
var StyledButton = styled_components_1.default.button.withConfig({
    displayName: "Button__StyledButton",
    componentId: "c7i75x-7"
})(["display:flex;align-items:center;justify-content:center;background:transparent;width:100%;height:100%;border:none;outline:none;appearance:none;cursor:", ";padding:", ";", "{padding:", ";}", "{padding:", ";}"], function (props) {
    return props.disabled ? 'not-allowed' : 'pointer';
}, function (props) {
    return props.size === 'regular' ? '0px 24px' : props.size === 'small' ? '0px 16px' : props.size === 'large' ? '0px 40px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('laptop'), function (props) {
    return props.size === 'regular' ? '0px 32px' : props.size === 'small' ? '0px 24px' : props.size === 'large' ? '0px 48px' : props.size === 'nav' ? '0px 40px' : 0;
}, _utils_1.mediaQuery('desktop'), function (props) {
    return props.size === 'regular' ? '0px 40px' : props.size === 'small' ? '0px 32px' : props.size === 'large' ? '0px 56px' : props.size === 'nav' ? '0px 40px' : 0;
}); // Typing
exports.default = Button;
